import React, { useEffect, useState } from 'react';
import { Calendar, Plus, X, Save, Edit2, Trash2, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import leaveTypesService, { LeaveType as LeaveTypeDto } from '../services/leaveTypesService';
import { uyar, hata, onayla } from '../components/GlobalModal';

const emptyForm: Partial<LeaveTypeDto> = {
  typeName: '',
  description: '',
  defaultDays: 14,
  isPaid: true,
  requiresApproval: true,
  isActive: true,
};

const LeaveTypes: React.FC = () => {
  const [items, setItems] = useState<LeaveTypeDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<LeaveTypeDto | null>(null);
  const [form, setForm] = useState<Partial<LeaveTypeDto>>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await leaveTypesService.list(true);
      if (res.success) setItems(res.data as any);
    } catch (e) {
      setMsg({ ok: false, text: 'İzin türleri yüklenemedi' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setMsg(null);
    setShowModal(true);
  };

  const openEdit = (lt: LeaveTypeDto) => {
    setEditing(lt);
    setForm({ ...lt });
    setMsg(null);
    setShowModal(true);
  };

  const save = async () => {
    if (!form.typeName?.trim()) {
      setMsg({ ok: false, text: 'Tür adı boş olamaz' });
      return;
    }
    setSaving(true);
    try {
      const res = editing
        ? await leaveTypesService.update(editing.leaveTypeID, form)
        : await leaveTypesService.create(form);
      if (res.success) {
        await load();
        setShowModal(false);
        setMsg({ ok: true, text: editing ? 'Güncellendi' : 'Eklendi' });
        setTimeout(() => setMsg(null), 3000);
      } else {
        setMsg({ ok: false, text: (res as any).message || 'İşlem başarısız' });
      }
    } catch (e: any) {
      setMsg({ ok: false, text: e?.response?.data?.message || 'Hata oluştu' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (lt: LeaveTypeDto) => {
    if (!(await onayla(`"${lt.typeName}" türünü silmek istiyor musun?`))) return;
    try {
      await leaveTypesService.delete(lt.leaveTypeID);
      await load();
      setMsg({ ok: true, text: 'Silindi (kullanımdaysa pasif hale getirildi)' });
      setTimeout(() => setMsg(null), 3000);
    } catch (e) {
      setMsg({ ok: false, text: 'Silinemedi' });
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-blue-100 text-blue-600 flex items-center justify-center">
            <Calendar size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-black text-slate-800">İzin Türleri</h1>
            <p className="text-slate-500 text-sm">Yıllık izin, hastalık, mazeret vb. tür tanımları</p>
          </div>
        </div>
        <button
          onClick={openAdd}
          className="px-4 py-2.5 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 flex items-center gap-2 transition-all"
        >
          <Plus size={16} />
          Yeni İzin Türü
        </button>
      </div>

      {msg && (
        <div className={`px-4 py-3 rounded-xl text-sm flex items-center gap-2 ${
          msg.ok ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
                 : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {msg.ok ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
          {msg.text}
        </div>
      )}

      {/* Tablo */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-12 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          </div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-slate-400 text-sm">Henüz kayıt yok</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                <th className="px-4 py-3">Tür Adı</th>
                <th className="px-4 py-3">Açıklama</th>
                <th className="px-4 py-3 text-center">Yıllık Gün</th>
                <th className="px-4 py-3 text-center">Ücretli</th>
                <th className="px-4 py-3 text-center">Onay Gerek</th>
                <th className="px-4 py-3 text-center">Durum</th>
                <th className="px-4 py-3 text-right">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {items.map(lt => (
                <tr key={lt.leaveTypeID} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-bold text-slate-800">{lt.typeName}</td>
                  <td className="px-4 py-3 text-slate-500">{lt.description || '-'}</td>
                  <td className="px-4 py-3 text-center">{lt.defaultDays ?? '-'}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${lt.isPaid ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                      {lt.isPaid ? 'Evet' : 'Hayır'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${lt.requiresApproval ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                      {lt.requiresApproval ? 'Evet' : 'Hayır'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${lt.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                      {lt.isActive ? 'Aktif' : 'Pasif'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right space-x-1">
                    <button onClick={() => openEdit(lt)} className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-all"><Edit2 size={14} /></button>
                    <button onClick={() => remove(lt)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-black text-slate-800">{editing ? 'İzin Türü Düzenle' : 'Yeni İzin Türü'}</h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-slate-100 rounded-lg"><X size={16} /></button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Tür Adı *</label>
                <input
                  type="text"
                  value={form.typeName ?? ''}
                  onChange={(e) => setForm({ ...form, typeName: e.target.value })}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:outline-none text-sm"
                  placeholder="Örn: Yıllık İzin"
                />
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Açıklama</label>
                <textarea
                  rows={2}
                  value={form.description ?? ''}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:outline-none text-sm resize-none"
                />
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Yıllık Varsayılan Gün</label>
                <input
                  type="number"
                  value={form.defaultDays ?? 0}
                  onChange={(e) => setForm({ ...form, defaultDays: parseInt(e.target.value) || 0 })}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:outline-none text-sm"
                />
              </div>
              <div className="flex items-center gap-4 flex-wrap">
                <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                  <input type="checkbox" checked={!!form.isPaid} onChange={(e) => setForm({ ...form, isPaid: e.target.checked })} />
                  Ücretli
                </label>
                <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                  <input type="checkbox" checked={!!form.requiresApproval} onChange={(e) => setForm({ ...form, requiresApproval: e.target.checked })} />
                  Onay Gerektirir
                </label>
                <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                  <input type="checkbox" checked={!!form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
                  Aktif
                </label>
              </div>
            </div>

            <div className="flex gap-2 pt-3 border-t border-slate-100">
              <button onClick={() => setShowModal(false)} className="flex-1 py-2.5 bg-white border border-slate-200 text-slate-600 rounded-xl font-bold hover:bg-slate-50">
                İptal
              </button>
              <button onClick={save} disabled={saving} className="flex-1 py-2.5 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2">
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                Kaydet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveTypes;
